import React, { useContext } from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { PlainButton } from '../../components/pageStyles';
import { wz } from '../wizardStyles';
import { WizardJumpContext } from './WizardNav';

const Bar = styled.div`
  display: flex;
  width: 100%;

  > * + * {
    margin-left: 5px;
  }
`;

const Segment = styled.span<{ $lit: boolean }>`
  flex: 1 1 0;
  height: 3px;
  border-radius: 3px;
  background: ${({ $lit }) => ($lit ? wz.accent : wz.border)};
`;

// An earlier segment is a way back to its step; a 3 px bar keeps a taller hit area around it.
const Jump = styled(PlainButton)`
  display: flex;
  flex: 1 1 0;
  align-items: center;
  padding: 6px 0;
  margin: -6px 0;

  > span {
    flex: 1 1 auto;
  }

  &:focus-visible {
    outline: 2px solid ${wz.accent};
    outline-offset: 2px;
  }
`;

/** Five segments, the first `lit` drawn in the accent; earlier ones jump back to their step. */
const ProgressSegments: React.FC<{ lit: number; total?: number }> = ({ lit, total = 5 }) => {
  const segments = Array.from({ length: total }, (_, index) => index + 1);
  const jump = useContext(WizardJumpContext);
  const { t } = useMultiLanguageTranslation();
  return (
    <Bar
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={lit}
      data-testid="orbit-wizard-progress"
    >
      {segments.map(segment =>
        jump !== null && segment < lit ? (
          <Jump
            key={segment}
            type="button"
            aria-label={t(`${TOrbit}.wizard.jump`, { step: segment, total })}
            onClick={() => jump(segment)}
          >
            <Segment $lit data-lit />
          </Jump>
        ) : (
          <Segment key={segment} $lit={segment <= lit} data-lit={segment <= lit} />
        )
      )}
    </Bar>
  );
};

export default ProgressSegments;
